import { FC, useEffect, useMemo, useState } from "react";
import useOrderCargoOrderDriverSimulation from "../../../../../common/services/order/cargo-order/driver-simulation/use-order-cargo-order-driver-simulation";
import useAbort from "../../../../../common/hooks/use-abort";
import FormFieldComponent from "../../../../../common/components/form/field/form-field.component";
import ButtonComponent from "../../../../../common/components/button/button.component";
import userService from "../../../../../common/services/user/user.service";
import OrderDetailsManageDriverAssignmentDriver from "../common/types/order-details-manage-driver-assignment-driver";
import orderTranslationsHelper from "../../../../../languages/order-translations.helper";
import LoaderComponent from "../../../../../common/components/loader/loader.component";
import ErrorComponent from "../../../../../common/components/error/error.component";
import { useAppContext } from "../../../../../context/app.context";
import OrderDetailsManageDriverAssignmentSimulationTableComponent from "./table/order-details-manage-driver-assignment-simulation-table.component";
import TextareaInputComponent from "../../../../../common/components/form/input/textarea/textarea-input.component";
import notificationService from "../../../../../common/utils/notification/notification.service";

type Props = {
  orderId: string;
  driver: OrderDetailsManageDriverAssignmentDriver | undefined;
};

const OrderDetailsManageDriverAssignmentSimulationComponent: FC<Props> = (
  props,
) => {
  const driverSimulation = useOrderCargoOrderDriverSimulation();
  const driverSimulationAbort = useAbort();
  const saveDriverNotesAbort = useAbort();

  const { selectedAppLanguage } = useAppContext();

  const translations =
    orderTranslationsHelper.getDetailsTranslations().manageDriverAssignment
      .extended.simualtor;

  const [driverNotes, setDriverNotes] = useState("");
  const [lowestCostModel, setLowestCostModel] = useState<"ABA" | "SABS">("ABA");

  useEffect(() => {
    if (!props.driver) return;

    driverSimulation.load(
      {
        driverUuid: props.driver.uuid,
        orderUuid: props.orderId,
      },
      driverSimulationAbort.signal,
    );

    return driverSimulationAbort.revoke;
  }, [props.driver, props.orderId]);

  useEffect(() => {
    if (!driverSimulation.data) return;

    setDriverNotes(driverSimulation.data.driverNotes ?? "");

    const abaCost = driverSimulation.data.simulations.aba.totalCost;
    const sabsCost = driverSimulation.data.simulations.sabs.totalCost;

    setLowestCostModel(abaCost < sabsCost ? "ABA" : "SABS");
  }, [driverSimulation.data]);

  const preparedDriverMessage = useMemo(
    () =>
      translations.preparedDriverMessageTemplates[
        Math.round(
          (translations.preparedDriverMessageTemplates.length - 1) *
            Math.random(),
        )
      ]
        .replace("${driverName}", `${props.driver?.firstName}`)
        .replace("${model}", lowestCostModel),
    [selectedAppLanguage],
  );

  const saveDriverNotes = () => {
    if (!props.driver) return;

    userService
      .editDriverNotes(
        {
          userUuid: props.driver.uuid,
          notes: driverNotes,
        },
        saveDriverNotesAbort.signal,
      )
      .then(() => {
        notificationService.success(
          translations.editSuccessNotificationMessage,
        );
      })
      .catch(() => {
        notificationService.error(translations.editFailureNotificationMessage);
      });
  };

  if (!props.driver) return null;

  if (!driverSimulation.data) {
    return (
      <div className="order_details_manage_driver_assignment_extended_simulator">
        {driverSimulation.isLoading ? (
          <LoaderComponent type="primary" />
        ) : (
          <ErrorComponent
            message={translations.simulationDownloadErrorMessage}
          />
        )}
      </div>
    );
  }

  return (
    <div className="order_details_manage_driver_assignment_extended_simulator">
      <div className="order_details_manage_driver_assignment_extended_simulator_row">
        <div className="order_details_manage_driver_assignment_extended_simulator_column">
          <FormFieldComponent
            label={translations.contractNotesLabel}
            classNames={{ root: "mt-0" }}
          >
            <TextareaInputComponent
              placeholder={translations.contractNotesPlaceholder}
              value={driverSimulation.data.contractNotes ?? ""}
              isReadOnly
            />
          </FormFieldComponent>
        </div>
        <div className="order_details_manage_driver_assignment_extended_simulator_column">
          <FormFieldComponent
            label={translations.driverNotesLabel}
            classNames={{ root: "mt-0" }}
          >
            <TextareaInputComponent
              placeholder={translations.driverNotesPlaceholder}
              value={driverNotes}
              onChange={setDriverNotes}
            />
            <ButtonComponent onClick={saveDriverNotes} type="primary">
              {translations.driverNotesSaveButtonLabel}
            </ButtonComponent>
          </FormFieldComponent>
        </div>
      </div>
      <div className="order_details_manage_driver_assignment_extended_simulator_row">
        <OrderDetailsManageDriverAssignmentSimulationTableComponent
          driverName={`${props.driver.firstName} ${props.driver.lastName}`}
          simulations={driverSimulation.data.simulations}
          chosenModel={driverSimulation.data.model}
        />
      </div>
      <div className="order_details_manage_driver_assignment_extended_simulator_row">
        <FormFieldComponent
          label={translations.preparedDriverMessageLabel}
          classNames={{
            root: "textarea_prepared_message_form_field",
          }}
        >
          <TextareaInputComponent
            placeholder={translations.preparedDriverMessagePlaceholder}
            value={preparedDriverMessage}
            classNames={{
              root: "textarea_prepared_message",
            }}
            isReadOnly
          />
        </FormFieldComponent>
      </div>
    </div>
  );
};

export default OrderDetailsManageDriverAssignmentSimulationComponent;
